import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light'); // 'light' or 'dark'
  const [primaryColor, setPrimaryColor] = useState('primary');

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    const savedPrimaryColor = localStorage.getItem('primaryColor') || 'primary';

    setTheme(savedTheme);
    setPrimaryColor(savedPrimaryColor);

    // Apply theme to document
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Save theme changes to localStorage
  const updateTheme = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const updatePrimaryColor = (newColor) => {
    setPrimaryColor(newColor);
    localStorage.setItem('primaryColor', newColor);
  };

  // Theme color mappings
  const themeColors = {
    primary: {
      50: '#ecf0f1',
      100: '#d5dbdb',
      200: '#abb7b7',
      300: '#829393',
      400: '#59707a',
      500: '#34495e',
      600: '#2c3e50',
      700: '#233140',
      800: '#1a2530',
      900: '#111820',
    },
    blue: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
    },
    green: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
    },
    purple: {
      50: '#faf5ff',
      100: '#f3e8ff',
      200: '#e9d5ff',
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7c3aed',
      800: '#6b21a8',
      900: '#581c87',
    }
  };

  const value = {
    theme,
    primaryColor,
    themeColors,
    updateTheme,
    updatePrimaryColor,
    colors: themeColors[primaryColor] || themeColors.primary
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;